import { useState } from "react";
import { API } from "../../../services/api";
import { validateUserCredentials } from "../../../utils/userValidation";
import { apiErrorMessage, EMPTY_USER_FORM, userEditPayload } from "../superAdminRules";

const EMPTY_EDIT_USER_FORM = { email: "", role: "PHARMACIEN", password: "" };

function useUserEditor(setUsers) {
    const [editingUserId, setEditingUserId] = useState(null);
    const [editUserForm, setEditUserForm] = useState(EMPTY_EDIT_USER_FORM);
    const [editUserError, setEditUserError] = useState(null);
    const [editUserSaving, setEditUserSaving] = useState(false);

    const startEditUser = (u) => {
        setEditingUserId(u.id);
        setEditUserForm({ email: u.email, role: u.role, password: "" });
        setEditUserError(null);
    };

    const handleEditUser = async (e) => {
        e.preventDefault();
        setEditUserError(null);
        const validationError = validateUserCredentials({
            email: editUserForm.email,
            password: editUserForm.password,
            passwordRequired: false,
        });
        if (validationError) {
            setEditUserError(validationError);
            return;
        }
        setEditUserSaving(true);
        try {
            const { data } = await API.patch(`/super-admin/users/${editingUserId}`, userEditPayload(editUserForm));
            setUsers(prev => prev.map(u => u.id === editingUserId ? data : u));
            setEditingUserId(null);
        } catch (err) {
            setEditUserError(apiErrorMessage(err, "Erreur lors de la sauvegarde."));
        } finally {
            setEditUserSaving(false);
        }
    };

    return {
        editingUserId,
        editUserForm,
        setEditUserForm,
        editUserError,
        editUserSaving,
        startEditUser,
        cancelEditUser: () => setEditingUserId(null),
        handleEditUser,
    };
}

function useUserCreation(selectedPharmacyId, setUsers) {
    const [userForm, setUserForm] = useState(EMPTY_USER_FORM);
    const [userSubmitting, setUserSubmitting] = useState(false);
    const [userFormError, setUserFormError] = useState(null);

    const handleUserSubmit = async (e) => {
        e.preventDefault();
        setUserFormError(null);
        const validationError = validateUserCredentials(userForm);
        if (validationError) {
            setUserFormError(validationError);
            return;
        }
        setUserSubmitting(true);
        try {
            const { data } = await API.post("/super-admin/users", {
                ...userForm,
                pharmacyId: Number(selectedPharmacyId),
            }, { noRedirectOn401: true });
            setUsers(prev => [...prev, data]);
            setUserForm(EMPTY_USER_FORM);
        } catch (err) {
            setUserFormError(apiErrorMessage(err, "Erreur lors de la création."));
        } finally {
            setUserSubmitting(false);
        }
    };

    return { userForm, setUserForm, userSubmitting, userFormError, setUserFormError, handleUserSubmit };
}

export function usePharmacyUsers() {
    const [selectedPharmacyId, setSelectedPharmacyId] = useState("");
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersError, setUsersError] = useState(null);
    const creation = useUserCreation(selectedPharmacyId, setUsers);
    const editor = useUserEditor(setUsers);

    const fetchUsers = async (pharmacyId) => {
        if (!pharmacyId) { setUsers([]); return; }
        setUsersLoading(true);
        setUsersError(null);
        try {
            const { data } = await API.get("/super-admin/users", { params: { pharmacyId } });
            setUsers(data);
        } catch {
            setUsersError("Impossible de charger les utilisateurs.");
        } finally {
            setUsersLoading(false);
        }
    };

    const handlePharmacySelect = (e) => {
        const id = e.target.value;
        setSelectedPharmacyId(id);
        creation.setUserFormError(null);
        fetchUsers(id || null);
    };

    const handleDeleteUser = async (userId) => {
        if (!globalThis.confirm("Supprimer cet utilisateur ?")) return;
        try {
            await API.delete(`/super-admin/users/${userId}`);
            setUsers(prev => prev.filter(u => u.id !== userId));
        } catch {
            alert("Erreur lors de la suppression.");
        }
    };

    return {
        selectedPharmacyId,
        users,
        usersLoading,
        usersError,
        handlePharmacySelect,
        handleDeleteUser,
        ...creation,
        ...editor,
    };
}
